import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SendResultsView } from '../../components/sendResults/SendResultsView';
import { ApiError, isStillRunningError } from '../../lib/api';
import { getPreviewSendReport, retryFailedInBatch, retrySend, type SendResult } from './sendApi';
import { previewSendBatchToResultsData } from './previewSendResults';
import { SendResultToast } from './SendResultToast';

// Stage 6A v5 §3.1 — preview send-results report at /preview-sends/:batchId.
// Fetches the assembled report, adapts to SendResultsData, attaches the
// preview-specific retry actions (Q1), and renders the shared <SendResultsView>.
// Polls every 10s while the batch is in_progress.
//
// Retry creates a NEW batch (the original failed sends stay for audit), so a
// success surfaces a toast pointing at the outcome rather than mutating this
// report's rows.

export function PreviewSendReport() {
  const { batchId } = useParams<{ batchId: string }>();
  const qc = useQueryClient();
  const [retryResult, setRetryResult] = useState<SendResult | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  // A retry whose connection timed out (Cloudflare 524) is still sending on the
  // server. Its buttons stay disabled until the page is refreshed — re-pressing
  // would re-send to recipients the first retry is already reaching.
  const [retryStillRunning, setRetryStillRunning] = useState(false);

  const report = useQuery({
    queryKey: ['previewSendReport', batchId],
    queryFn: () => getPreviewSendReport(batchId!),
    enabled: Boolean(batchId),
    refetchInterval: (q) => (q.state.data?.batch.status === 'in_progress' ? 10000 : false),
  });

  const onRetrySuccess = (result: SendResult) => {
    setRetryResult(result);
    setRetryError(null);
    void report.refetch();
  };
  const onRetryError = (err: Error) => {
    if (isStillRunningError(err)) setRetryStillRunning(true);
    setRetryError(err instanceof ApiError ? err.message : 'Retry failed.');
  };

  const retryAll = useMutation({
    mutationFn: () => retryFailedInBatch(batchId!),
    onSuccess: onRetrySuccess,
    onError: onRetryError,
  });
  const retryOne = useMutation({
    mutationFn: (sendId: string) => retrySend(sendId),
    onSuccess: onRetrySuccess,
    onError: onRetryError,
  });

  if (report.isLoading) return <CenteredSpinner />;
  if (report.error || !report.data) {
    return <p className="text-sm text-red-600">Couldn't load this report.</p>;
  }

  const { batch, sends, links, title, audienceDescription } = report.data;
  const base = previewSendBatchToResultsData(batch, sends, links, { title, audienceDescription });
  const failedCount = sends.filter(
    (s) => s.status === 'failed' || s.status === 'bounced',
  ).length;

  const data = {
    ...base,
    actions: {
      retryAllFailed: {
        onClick: () => retryAll.mutate(),
        pending: retryAll.isPending || retryStillRunning,
        failedCount,
        errorText: retryError,
      },
      retryRecipient: (recipient: { sendId?: string }) => {
        if (recipient.sendId && !retryStillRunning) retryOne.mutate(recipient.sendId);
      },
    },
  };

  return (
    <>
      <SendResultsView data={data} backHref={`/rollouts/${batch.rolloutId}`} />
      {retryResult && (
        <SendResultToast result={retryResult} onDismiss={() => setRetryResult(null)} />
      )}
    </>
  );
}
